// Multi-user & roles — client-side mirror of server/roles.js (docs/multi-
// user-roles-spec.md §3). UI GATING ONLY — this is convenience, never
// enforcement (spec I2): every mutating request is re-checked server-side by
// requireCapability(), so a mismatch here can only ever hide a control the
// server would still refuse, never expose one the server would allow.
//
// Keep this in lockstep with server/roles.js's MATRIX — same capabilities,
// same allowed roles per capability. If you change one, change both.
import { state } from "./state.js";
import { ROLE_WORDS } from "../../../shared/team.js";

export const ROLES = ["owner", "admin", "member", "viewer", "field"];

const MATRIX = {
  "documents.read":      ["owner", "admin", "member", "viewer"],
  "documents.write":     ["owner", "admin", "member"],
  "clients.write":       ["owner", "admin", "member"],
  "jobs.read":           ["owner", "admin", "member", "viewer", "field"],
  "visits.work":         ["owner", "admin", "member", "field"],
  "company.settings":    ["owner", "admin"],
  "company.export":      ["owner", "admin"],
  "stripe.connect":      ["owner", "admin"],
  "accounting.connect":  ["owner", "admin"],
  "billing.view":        ["owner", "admin"],
  "billing.update_card": ["owner", "admin"],
  "billing.reenable":    ["owner", "admin"],
  "billing.cancel":      ["owner"],
  "team.read":            ["owner", "admin", "member", "viewer"],
  "team.invite":          ["owner", "admin"],
  "team.rerole":          ["owner", "admin"],
  "team.remove":          ["owner", "admin"],
  "team.leave":            ["admin", "member", "viewer", "field"],
  "company.transfer":    ["owner"],
  "company.delete":      ["owner"]
};

// Every capability this mirror knows, so test/roles.test.mjs can hold its list
// to the server's as well as its cells: one added here alone would otherwise
// pass, because roleHasCapability answers false for a name it doesn't know.
export const CAPABILITIES = Object.freeze(Object.keys(MATRIX));

export function roleHasCapability(role, capability) {
  const allowed = MATRIX[capability];
  if (!allowed) return false;
  return allowed.includes(role);
}

// Convenience for screens: reads the role off state.membership directly, so
// call sites read as `if (can("documents.write")) { ... }` without having to
// thread the role through. A user with no membership (mid-onboarding, or a
// removed/companyless session) can do nothing.
export function can(capability) {
  const role = state.membership?.role;
  if (!role) return false;
  return roleHasCapability(role, capability);
}

// True once state.membership.role resolves to something other than owner
// with a single company — used for the "View only" topbar pill and other
// viewer-specific UI (spec §6.4).
export function isViewer() {
  return state.membership?.role === "viewer";
}

// A Field person (ADR-041): their visits and jobs are in the iPhone app, and
// the web has nothing else for them, so every route but their profile shows
// them where to go (screens/field-home.js).
export function isField() {
  return state.membership?.role === "field";
}

// Shared role display bits — the Team screen and the shell's company
// switcher both render role tags, so this lives in one place rather than
// two hand-maintained copies that could silently drift.
// The names are shared/team.js's, which the iPhone says too.
export const ROLE_LABELS = Object.fromEntries(Object.entries(ROLE_WORDS).map(([role, words]) => [role, words.label]));

export function roleLabel(role) {
  return ROLE_LABELS[role] || "Member";
}

// "Field: sees their own visits and jobs, and no prices. Free." What a role
// means, as the invite drawer says it under the role it's sending.
export function roleLine(role) {
  const words = ROLE_WORDS[role] || ROLE_WORDS.member;
  return `${words.label}: ${words.description.charAt(0).toLowerCase()}${words.description.slice(1)}`;
}

// Owner=blue, Admin/Member=grey, Viewer and Field=green (the free ones) — see
// design-system.html's Team story.
export const ROLE_PILL_CLASS = { owner: "app-pill-blue", admin: "app-pill-grey", member: "app-pill-grey", viewer: "app-pill-green", field: "app-pill-green" };

export function rolePillClass(role) {
  return ROLE_PILL_CLASS[role] || "app-pill-grey";
}
